// @/src/core/utils/store.utils.ts

/**
 * @file src/core/utils/store.utils.ts
 * @description Utilidad genérica para la creación de stores de estado.
 */

/**
 * Clase base para stores de estado con funcionalidad común
 */
export abstract class StoreBase<T> {
  private state: T;
  private listeners: Set<(state: T) => void> = new Set();

  constructor(initialState: T) {
    this.state = initialState;
  }

  /**
   * Obtiene el estado actual
   */
  getState(): T {
    return this.state;
  }

  /**
   * Suscribe un listener a los cambios de estado
   * @param listener Función que se ejecuta cuando cambia el estado
   * @returns Función para desuscribirse
   */
  subscribe(listener: (state: T) => void): (() => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Actualiza el estado parcialmente y notifica a los listeners
   * @param partialState Estado parcial para actualizar
   */
  protected updateState(partialState: Partial<T>): void {
    this.state = { ...this.state, ...partialState };
    this.notifyListeners();
  }

  /**
   * Reemplaza completamente el estado y notifica a los listeners
   * @param newState Nuevo estado completo
   */
  protected setState(newState: T): void {
    this.state = newState;
    this.notifyListeners();
  }

  /**
   * Resetea el estado al estado inicial
   */
  protected resetState(): void {
    this.state = this.getInitialState();
    this.notifyListeners();
  }

  /**
   * Notifica a todos los listeners sobre el cambio de estado
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.state));
  }

  /**
   * Método abstracto para obtener el estado inicial
   * Las subclases deben implementar este método
   */
  protected abstract getInitialState(): T;
}

/**
 * Crea un store de estado simple con suscripciones.
 * @param initialState El estado inicial del store.
 * @returns Un objeto de store con métodos para interactuar con el estado.
 */
export function createStore<T>(initialState: T) {
    let state: T = initialState;
    const listeners: Set<(state: T) => void> = new Set();

    /**
     * Devuelve el estado actual.
     * @returns El estado actual.
     */
    const getState = (): T => state;

    /**
     * Suscribe un listener a los cambios de estado.
     * @param listener La función a llamar cuando el estado cambie.
     * @returns Una función para desuscribirse.
     */
    const subscribe = (listener: (state: T) => void): (() => void) => {
        listeners.add(listener);
        return () => listeners.delete(listener); // Devuelve la función de desuscripción
    };

    /**
     * Actualiza el estado y notifica a los listeners.
     * @param updater Una función que recibe el estado actual y devuelve el nuevo estado.
     */
    const update = (updater: (currentState: T) => T): void => {
        state = updater(state);
        listeners.forEach(listener => listener(state));
    };
    
    return {
        getState,
        subscribe,
        update,
    };
} 